import { useMemo, useState } from 'react';
import TravelGlobe from '../components/TravelGlobe';
import { pickLocalized, useLang, useT } from '../i18n';
import { travelRoutes, travelStops, type TravelStop } from '../data/travel';

const EARTH_TEXTURE_URL = '/textures/earth/2k_earth_daymap.jpg';

export default function TravelMap() {
  const t = useT();
  const { lang } = useLang();
  const [selectedStop, setSelectedStop] = useState<TravelStop | null>(travelStops[0] ?? null);
  const stopById = useMemo(() => new Map(travelStops.map((stop) => [stop.id, stop])), []);

  return (
    <section className="flex flex-col gap-8">
      <header className="max-w-3xl flex flex-col gap-3">
        <p className="font-mono text-xs uppercase tracking-[0.24em] text-coffee">
          {t('travelMap.kicker')}
        </p>
        <h2 className="text-3xl md:text-5xl font-semibold text-cocoa leading-tight">
          {t('travelMap.title')}
        </h2>
        <p className="text-coffee leading-relaxed">{t('travelMap.subtitle')}</p>
      </header>

      <article className="rounded-xl border border-neutral-200 bg-white p-5 md:p-6 flex flex-col gap-5 shadow-soft transition-shadow duration-200 hover:shadow-float">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-coffee">
              {t('travelMap.globeLabel')}
            </p>
            <h3 className="mt-2 text-2xl font-semibold text-cocoa">
              {t('travelMap.globeTitle')}
            </h3>
          </div>
          <span className="font-mono text-xs text-coffee/70">
            {t('travelMap.dragHint')}
          </span>
        </div>

        <TravelGlobe
          stops={travelStops}
          routes={travelRoutes}
          earthTextureUrl={EARTH_TEXTURE_URL}
          ariaLabel={t('travelMap.globeAriaLabel')}
          fallbackText={t('travelMap.webglFallback')}
          loadingText={t('common.loading')}
          onSelectStop={setSelectedStop}
        />

        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 shadow-sm">
          <p className="font-mono text-[10px] uppercase tracking-wider text-coffee">
            {t('travelMap.selectedCityLabel')}
          </p>
          {selectedStop ? (
            <div className="mt-2 flex flex-col gap-1">
              <p className="text-2xl font-semibold text-cocoa">
                {pickLocalized(selectedStop.city, lang)}
              </p>
              <p className="text-sm text-coffee">
                {pickLocalized(selectedStop.country, lang)} · {selectedStop.year} · {selectedStop.lat.toFixed(2)}, {selectedStop.lng.toFixed(2)}
              </p>
            </div>
          ) : (
            <p className="mt-2 text-sm text-coffee">{t('travelMap.noCitySelected')}</p>
          )}
        </div>

        <p className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-3 text-xs leading-relaxed text-coffee">
          {t('travelMap.textureAttribution')}
        </p>

        <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
          <section className="rounded-lg border border-neutral-200 bg-white p-4 shadow-soft transition-shadow duration-200 hover:shadow-float">
            <div className="flex items-center justify-between gap-3">
              <p className="font-mono text-[10px] uppercase tracking-wider text-coffee">
                {t('travelMap.stopsLabel')}
              </p>
              <span className="font-mono text-xs text-coffee/70">
                {travelStops.length}
              </span>
            </div>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {travelStops.map((stop) => {
                const active = stop.id === selectedStop?.id;
                return (
                  <li key={stop.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedStop(stop)}
                      className={[
                        'w-full rounded-xl border px-3 py-2 text-left shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15',
                        active
                          ? 'border-neutral-950 bg-neutral-100 shadow-soft'
                          : 'border-neutral-200 bg-white hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-soft',
                      ].join(' ')}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-semibold text-cocoa">
                          {pickLocalized(stop.city, lang)}
                        </span>
                        <span className="font-mono text-[10px] text-coffee">{stop.year}</span>
                      </div>
                      <p className="mt-1 text-xs text-coffee">
                        {pickLocalized(stop.country, lang)} · {stop.lat.toFixed(2)}, {stop.lng.toFixed(2)}
                      </p>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="rounded-lg border border-neutral-200 bg-white p-4 shadow-soft transition-shadow duration-200 hover:shadow-float">
            <div className="flex items-center justify-between gap-3">
              <p className="font-mono text-[10px] uppercase tracking-wider text-coffee">
                {t('travelMap.routesLabel')}
              </p>
              <span className="font-mono text-xs text-coffee/70">
                {travelRoutes.length}
              </span>
            </div>
            <ol className="mt-4 flex flex-col gap-3">
              {travelRoutes.map(([fromId, toId], index) => {
                const from = stopById.get(fromId);
                const to = stopById.get(toId);
                if (!from || !to) return null;

                return (
                  <li key={`${fromId}-${toId}`} className="flex items-center gap-3 text-sm text-coffee">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-neutral-300 bg-neutral-50 font-mono text-[10px] text-coffee">
                      {index + 1}
                    </span>
                    <span>
                      <span className="text-cocoa">{pickLocalized(from.city, lang)}</span>
                      <span className="px-2 text-neutral-400">→</span>
                      <span className="text-cocoa">{pickLocalized(to.city, lang)}</span>
                    </span>
                  </li>
                );
              })}
            </ol>
            <p className="mt-5 rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-4 text-sm leading-relaxed text-coffee">
              {t('travelMap.pinNote')}
            </p>
          </section>
        </div>
      </article>
    </section>
  );
}
